import { requestApi } from "../apiService";
import { ApiEndpoints } from "../apiEndpoints";
import {
  CreateGroupChatPayload,
  CreateP2pChatPayload,
  EnrichedChatList,
  GetEnrichedChatListPayload,
  GetSearchableGroupChatPayload,
  GroupChatResponseType,
  PublicGroupJoinRequestPayload,
  PublicGroupUsersRequests,
  SearchableGroupChatResponse,
} from "../types/chat";
import { GroupChatMember } from "../types/member";

// -------------- //
// ENRICHED CHATS //
// -------------- //

export const getEnrichedChats = async ({
  messages_take,
  chats_take,
  chats_skip,
}: GetEnrichedChatListPayload): Promise<EnrichedChatList> => {
  const response = await requestApi(
    {
      url: ApiEndpoints.ENRICHED_CHATS,
      method: "GET",
      params: {
        messages_take,
        chats_take,
        chats_skip,
      },
    },
    { retry429: true },
  );

  return response.data;
};

// -------------- //
// MUTED CHATS //
// -------------- //

export const getMutedChats = async (userName: string): Promise<string[]> => {
  const response = await requestApi(
    {
      url: ApiEndpoints.MUTED_CHATS,
      method: "GET",
      params: {
        username: userName,
      },
    },
    { retry429: true },
  );

  return response.data;
};

export const muteChatById = async (chatId: string): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.MUTE_CHAT,
    method: "POST",
    params: {
      chat_id: chatId,
    },
  });
};

export const unmuteChatById = async (chatId: string): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.UNMUTE_CHAT,
    method: "POST",
    params: {
      chat_id: chatId,
    },
  });
};

// -------------- //
// PINNED CHATS //
// -------------- //

export const pinChatById = async (
  chatId: string,
  isPinned: boolean,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.PIN_CHAT,
    method: "PATCH",
    params: {
      chat_id: chatId,
      is_pinned: isPinned,
    },
  });
};

// -------------- //
// ARCHIVED CHATS //
// -------------- //

export const archiveChatById = async (
  chatId: string,
  isArchived: boolean,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.ARCHIVE_CHAT,
    method: "PATCH",
    params: {
      chat_id: chatId,
      is_archived: isArchived,
    },
  });
};

// -------------- //
// PRIVATE CHATS //
// -------------- //

export const createP2PChat = async (
  payload: CreateP2pChatPayload,
): Promise<string> => {
  const response = await requestApi({
    url: ApiEndpoints.P2P_CHATS,
    method: "POST",
    data: payload,
  });

  return response.data;
};

export const deleteP2PChatById = async (
  chat_id: string,
  signal?: AbortSignal,
): Promise<void> => {
  await requestApi({
    url: `${ApiEndpoints.P2P_CHATS}/${chat_id}`,
    method: "DELETE",
    signal,
  });
};

// ------------ //
// GROUP CHATS //
// ------------ //

export const getGroupChatById = async (
  chatId: string,
): Promise<GroupChatResponseType> => {
  const response = await requestApi(
    {
      url: `${ApiEndpoints.CHAT}/${chatId}`,
      method: "GET",
      // params: {
      //   chat_id: chatId,
      // },
    },
    { retry429: true },
  );

  return response.data;
};

export const createGroupChat = async (
  payload: CreateGroupChatPayload,
): Promise<string> => {
  const response = await requestApi({
    url: ApiEndpoints.GROUP_CHATS,
    method: "POST",
    data: payload,
  });

  return response.data;
};

export const patchGroupChat = async (
  payload: GroupChatResponseType, // ??
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.CHAT,
    method: "PATCH",
    data: payload,
  });
};

export const getPublicGroupChats = async ({
  prefix,
  accessor,
  offset,
  count,
}: GetSearchableGroupChatPayload): Promise<SearchableGroupChatResponse[]> => {
  const response = await requestApi(
    {
      url: ApiEndpoints.SEARCHABLE_GROUP_CHATS,
      method: "GET",
      params: {
        prefix,
        accessor,
        offset,
        count,
      },
    },
    { retry429: true },
  );
  return response.data;
};

export const deleteGroupChatById = async (
  chat_id: string,
  signal?: AbortSignal,
): Promise<void> => {
  await requestApi({
    url: `${ApiEndpoints.CHAT}/${chat_id}`,
    method: "DELETE",
    signal,
  });
};

// --------------------------- //
// PUBLIC GROUP CHAT REQUESTS //
// --------------------------- //

export const sendPublicGroupJoinRequest = async (
  payload: PublicGroupJoinRequestPayload,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.PUBLIC_GROUP_JOIN_REQUEST,
    method: "POST",
    params: payload,
  });
};

export const acceptPublicGroupJoinRequest = async (
  payload: GroupChatMember,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.PUBLIC_GROUP_ACCEPT_REQUEST,
    method: "POST",
    data: payload,
  });
};

export const denyPublicGroupJoinRequest = async (
  chatId: string,
  username: string,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.PUBLIC_GROUP_DENY_REQUEST,
    method: "POST",
    params: {
      chat_id: chatId,
      username,
    },
  });
};

export const getPublicGroupUsersRequests = async (
  chatId: string,
): Promise<PublicGroupUsersRequests> => {
  const response = await requestApi(
    {
      url: ApiEndpoints.PUBLIC_GROUP_USERS_REQUESTS,
      method: "GET",
      params: {
        chat_id: chatId,
      },
    },
    { retry429: true },
  );

  return response.data;
};

export const getPublicGroupsUserRequests = async (): Promise<string[]> => {
  const response = await requestApi(
    {
      url: ApiEndpoints.PUBLIC_GROUPS_USER_REQUESTS,
      method: "GET",
    },
    { retry429: true },
  );

  return response.data;
};
